import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import femaleIcon from '../../../assets/female-icon.png';
import maleIcon from '../../../assets/male-icon.png';
import noDicomIcon from '../../../assets/nodicom-icon.png';

import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/api';
import {
  buildDicomSeriesKey,
  isReferenceMrSeries,
  isProjectReferenceSeries,
  mapApiSeriesToDisplaySeries,
  type DisplayDicomSeries,
} from '../../services/dicomSeriesAdapters';
import {
  analyzeDicomFiles,
  analyzeNiftiFile,
  deleteDicomUpload,
  type DicomTransferPhase,
} from '../../services/dicomService';
import {
  cachePreparedNiftiAsset,
  prepareSelectedSeriesData,
  type PreparedNiftiAsset,
} from '../../services/niftiPreparationService';
import { createPatient, uploadPatientPhoto } from '../../services/patientService';
import type { DicomAnalyzeResponse, DicomPatientSex } from '../../types/dicom';
import { formatDateDDMMYYYY, maskDateInput, toApiDate } from '../../utils/dateFormat';
import { getNiftiAlignmentLabel } from '../../utils/niftiAlignmentLabel';
import { publicUrl } from '../../utils/publicUrl';
import PatientPhoto from '../PatientPhoto';
import DicomSeriesThumbnail from './DicomSeriesThumbnail';
import {
  getDefaultOpenStudyId,
  groupReferenceSeriesByStudy,
  isHighMatchSeries,
} from './referenceSeries';
import './NewProjectModal.css';

interface NewProjectModalProps {
  onClose: () => void;
}

interface UploadedFileMeta {
  name: string;
  size: number;
  count: number;
}

interface SelectedFixedSeries {
  seriesInstanceUid: string;
  studyInstanceUid: string;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';
type UploadSource = 'files' | 'folder' | 'drop';
type ProjectImageSource = 'dicom' | 'nifti';
type DirectoryInputAttributes = React.InputHTMLAttributes<HTMLInputElement> & {
  webkitdirectory?: string;
  directory?: string;
};

type FileSystemEntryNode = {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
};

type FileSystemFileEntryNode = FileSystemEntryNode & {
  isFile: true;
  file: (
    successCallback: (file: File) => void,
    errorCallback?: (error: DOMException) => void
  ) => void;
};

type FileSystemDirectoryEntryNode = FileSystemEntryNode & {
  isDirectory: true;
  createReader: () => {
    readEntries: (
      successCallback: (entries: FileSystemEntryNode[]) => void,
      errorCallback?: (error: DOMException) => void
    ) => void;
  };
};

type WebkitDataTransferItem = DataTransferItem & {
  webkitGetAsEntry?: () => FileSystemEntryNode | null;
};

const DIRECTORY_INPUT_ATTRIBUTES: DirectoryInputAttributes = {
  webkitdirectory: '',
  directory: '',
};

const MODALITY_COLORS: Record<DisplayDicomSeries['modality'], string> = {
  T1w: 'modality-t1w',
  T2w: 'modality-t2w',
  FLAIR: 'modality-flair',
  DWI: 'modality-dwi',
  SWI: 'modality-swi',
  MRA: 'modality-unknown',
  CT: 'modality-ct',
  Unknown: 'modality-unknown',
};

const mapPatientSexToGender = (sex: DicomPatientSex | null) => {
  if (sex === 'M') {
    return 'Male';
  }

  if (sex === 'F') {
    return 'Female';
  }

  return '';
};

const formatSize = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const buildUploadMeta = (files: File[], source: UploadSource): UploadedFileMeta => {
  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  const firstRelativePath = files[0]?.webkitRelativePath ?? '';
  const folderName = firstRelativePath.split('/')[0] || 'Selected folder';
  const name =
    source === 'folder'
      ? folderName
      : files.length === 1
        ? files[0].name
        : `${files.length} files selected`;

  return {
    name,
    size: totalSize,
    count: files.length,
  };
};

const isAbortError = (error: unknown) =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';

const mapUploadErrorMessage = (error: unknown) => {
  if (isAbortError(error)) {
    return 'Request cancelled.';
  }

  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'Authentication expired.';
    }

    switch (error.code) {
      case 'no_dicom_files':
        return 'No DICOM files found.';
      case 'multiple_patients_detected':
        return 'Multiple patients detected.';
      case 'upload_too_large':
        return 'Upload too large.';
      case 'invalid_zip_archive':
        return 'Invalid ZIP archive.';
      case 'missing_required_uids':
        return 'No valid DICOM series found.';
      case 'invalid_nifti_upload':
      case 'unsupported_nifti_file':
      case 'invalid_nifti_file':
      case 'unsupported_nifti_dimensions':
      case 'invalid_nifti_geometry':
        return error.message;
      default:
        return error.message || 'Unknown analysis error.';
    }
  }

  if (error instanceof TypeError) {
    return 'Backend unavailable.';
  }

  return 'Unknown analysis error.';
};

const readFileEntry = (entry: FileSystemFileEntryNode) =>
  new Promise<File>((resolve, reject) => {
    entry.file(resolve, reject);
  });

const readDirectoryEntries = (
  entry: FileSystemDirectoryEntryNode
): Promise<FileSystemEntryNode[]> =>
  new Promise((resolve, reject) => {
    const reader = entry.createReader();
    const collectedEntries: FileSystemEntryNode[] = [];

    const readNextBatch = () => {
      reader.readEntries(nextEntries => {
        if (nextEntries.length === 0) {
          resolve(collectedEntries);
          return;
        }

        collectedEntries.push(...nextEntries);
        readNextBatch();
      }, reject);
    };

    readNextBatch();
  });

const collectFilesFromEntry = async (entry: FileSystemEntryNode): Promise<File[]> => {
  if (entry.isFile) {
    return [await readFileEntry(entry as FileSystemFileEntryNode)];
  }

  if (entry.isDirectory) {
    const childEntries = await readDirectoryEntries(entry as FileSystemDirectoryEntryNode);
    const childFiles = await Promise.all(
      childEntries.map(childEntry => collectFilesFromEntry(childEntry))
    );
    return childFiles.flat();
  }

  return [];
};

const collectDroppedFiles = async (dataTransfer: DataTransfer): Promise<File[]> => {
  const items = Array.from(dataTransfer.items ?? []) as WebkitDataTransferItem[];
  const entryItems = items
    .map(item => (item.webkitGetAsEntry?.() as FileSystemEntryNode | null | undefined) ?? null)
    .filter((entry): entry is FileSystemEntryNode => entry !== null);

  if (entryItems.length > 0) {
    const files = await Promise.all(entryItems.map(entry => collectFilesFromEntry(entry)));
    return files.flat();
  }

  return Array.from(dataTransfer.files ?? []);
};

interface ProjectInfoPanelProps {
  fullName: string;
  dob: string;
  gender: string;
  isNameAutofilled: boolean;
  isDobAutofilled: boolean;
  isGenderAutofilled: boolean;
  patientPhoto?: File | null;
  patientId?: string | null;
  hasStoredPhoto?: boolean;
  photoVersion?: string;
  token?: string | null;
  readOnly?: boolean;
  photoReadOnly?: boolean;
  showHeading?: boolean;
  onFullNameChange?: (value: string) => void;
  onDobChange?: (value: string) => void;
  onGenderChange?: (value: string) => void;
  onPatientPhotoChange?: (photo: File | null) => void;
}

export const ProjectInfoPanel: React.FC<ProjectInfoPanelProps> = ({
  fullName,
  dob,
  gender,
  isNameAutofilled,
  isDobAutofilled,
  isGenderAutofilled,
  patientPhoto,
  patientId,
  hasStoredPhoto = false,
  photoVersion,
  token,
  readOnly = false,
  photoReadOnly = readOnly,
  showHeading = true,
  onFullNameChange,
  onDobChange,
  onGenderChange,
  onPatientPhotoChange,
}) => {
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientPhoto) {
      setPhotoPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(patientPhoto);
    setPhotoPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [patientPhoto]);

  const acceptPatientPhoto = (nextPhoto: File | null) => {
    if (!nextPhoto) {
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(nextPhoto.type)) {
      setPhotoError('Use a JPEG, PNG, or WebP image.');
      return;
    }
    if (nextPhoto.size > 5 * 1024 * 1024) {
      setPhotoError('Patient photo must be 5 MB or smaller.');
      return;
    }
    setPhotoError(null);
    onPatientPhotoChange?.(nextPhoto);
  };

  const handlePhotoSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextPhoto = event.target.files?.[0] ?? null;
    event.target.value = '';
    acceptPatientPhoto(nextPhoto);
  };

  const photoPreview = photoPreviewUrl ? (
    <img
      src={photoPreviewUrl}
      alt="Selected patient"
      draggable={false}
    />
  ) : (
    <PatientPhoto
      patientId={patientId}
      hasPhoto={hasStoredPhoto}
      photoVersion={photoVersion}
      token={token}
      alt={`${fullName || 'Patient'} patient`}
      className="patient-photo-preview-image"
      fallback={
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
          <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
        </svg>
      }
    />
  );

  const patientPhotoContent = (
    <>
      <div className="patient-photo-preview">{photoPreview}</div>
      <div className="patient-photo-upload-copy">
        <strong>
          {patientPhoto?.name ??
            (hasStoredPhoto || photoReadOnly ? 'Patient photo' : 'Click to upload patient photo')}
        </strong>
        <span>
          {photoReadOnly
            ? hasStoredPhoto
              ? 'Uploaded patient image'
              : 'No patient photo uploaded'
            : 'JPEG, PNG or WebP · max 5 MB'}
        </span>
      </div>
      <svg
        className="patient-photo-upload-icon"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        {photoReadOnly ? (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11Z M8 14l2.5-3 2.5 3 3-4 2 5"
          />
        ) : (
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 002 2h10a2 2 0 002-2v-4"
          />
        )}
      </svg>
    </>
  );

  return (
    <section
      className={`project-panel patient-info-panel ${readOnly ? 'is-readonly' : ''} ${photoReadOnly ? 'is-photo-readonly' : ''}`}
    >
      {showHeading && (
        <div className="patient-info-panel-heading">
          <p className="panel-kicker">Patient Info</p>
          <h3>Patient Information</h3>
        </div>
      )}

      <div className="form-group">
        <label
          htmlFor="fullName"
          className="form-label"
        >
          Full Name
        </label>

        <input
          type="text"
          id="fullName"
          className={`form-input ${isNameAutofilled ? 'form-input-autofilled' : ''}`}
          placeholder="Name Surname"
          value={fullName}
          onChange={event => onFullNameChange?.(event.target.value)}
          readOnly={readOnly}
          required={!readOnly}
        />
      </div>

      <div className="form-row">
        <div className="form-group form-group-flex">
          <label
            htmlFor="dob"
            className="form-label"
          >
            Date of Birth
          </label>

          <input
            type="text"
            id="dob"
            className={`form-input ${isDobAutofilled ? 'form-input-autofilled' : ''}`}
            placeholder="DD.MM.YYYY"
            value={dob}
            inputMode="numeric"
            autoComplete="bday"
            maxLength={10}
            pattern="(?:0[1-9]|[12][0-9]|3[01])\.(?:0[1-9]|1[0-2])\.\d{4}"
            onChange={event => onDobChange?.(maskDateInput(event.target.value))}
            readOnly={readOnly}
            required={!readOnly}
          />
        </div>

        <div className="form-group form-group-gender">
          <label className="form-label">Gender</label>

          <div className="gender-options">
            <button
              type="button"
              className={`gender-button gender-button-male ${gender === 'Male' ? 'active' : ''}`}
              onClick={() => onGenderChange?.('Male')}
              title="Male"
              disabled={readOnly}
            >
              <img
                className="gender-icon"
                src={maleIcon}
                alt="Male"
                draggable={false}
              />
            </button>

            <button
              type="button"
              className={`gender-button gender-button-female ${gender === 'Female' ? 'active' : ''}`}
              onClick={() => onGenderChange?.('Female')}
              title="Female"
              disabled={readOnly}
            >
              <img
                className="gender-icon"
                src={femaleIcon}
                alt="Female"
                draggable={false}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="patient-photo-field">
        <span className="form-label">Patient Photo</span>
        {!photoReadOnly && (
          <input
            ref={photoInputRef}
            type="file"
            className="file-input-hidden"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoSelection}
          />
        )}
        {photoReadOnly ? (
          <div className="patient-photo-upload-area is-readonly">{patientPhotoContent}</div>
        ) : (
          <div
            className="patient-photo-upload-area"
            onDragOver={event => event.preventDefault()}
            onDrop={event => {
              event.preventDefault();
              acceptPatientPhoto(event.dataTransfer.files?.[0] ?? null);
            }}
          >
            <button
              type="button"
              className="patient-photo-upload-main"
              onClick={() => photoInputRef.current?.click()}
              aria-label={hasStoredPhoto ? 'Change patient photo' : 'Upload patient photo'}
              title={hasStoredPhoto ? 'Change patient photo' : 'Upload patient photo'}
            >
              {patientPhotoContent}
            </button>
            {patientPhoto && (
              <button
                type="button"
                className="patient-photo-remove-button"
                onClick={() => {
                  setPhotoError(null);
                  onPatientPhotoChange?.(null);
                }}
                aria-label="Remove patient photo"
                title="Remove patient photo"
              >
                ×
              </button>
            )}
          </div>
        )}
        {photoError && <span className="patient-photo-error">{photoError}</span>}
      </div>
    </section>
  );
};

type DicomSeriesActionMode = 'create-project' | 'select-series';

interface DicomSeriesPanelProps {
  isSelectionLocked?: boolean;
  imageSource?: ProjectImageSource;
  uploadedFile: UploadedFileMeta | null;
  uploadStatus: UploadStatus;
  uploadProgress: number;
  uploadPhase?: DicomTransferPhase;
  uploadError: string | null;
  uploadId: string | null;
  token: string;
  seriesList: DisplayDicomSeries[];
  selectedFixedSeriesKey: string | null;
  expandedSeriesKey?: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  folderInputRef: React.RefObject<HTMLInputElement | null>;
  onSelectSeries: (series: DisplayDicomSeries) => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFolderInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onUploadAreaClick: () => void;
  onRemoveUpload: () => void;
  isCreateDisabled: boolean;
  isSavingProject: boolean;
  createError: string | null;
  className?: string;
  actionMode?: DicomSeriesActionMode;
  allowIneligibleSelection?: boolean;
  thumbnailFallback?: (series: DisplayDicomSeries) => React.ReactNode;
  onConfirmSelection?: (series?: DisplayDicomSeries) => void;
  onRemoveSelection?: () => void;
  promotedSeriesKey?: string | null;
  isUploadRemovable?: boolean;
  seriesTypeLabel?: string;
  confirmedSeriesKey?: string | null;
  removeActionIcon?: 'close' | 'trash';
  displayVariant?: 'aligned' | 'original';
  onDisplayVariantChange?: (variant: 'aligned' | 'original') => void;
  isAlignedAvailable?: boolean;
}

interface DicomSeriesCardProps {
  selectionLocked?: boolean;
  series: DisplayDicomSeries;
  isSelected: boolean;
  isExpanded?: boolean;
  uploadId: string | null;
  token: string;
  isCreateDisabled: boolean;
  isSavingProject: boolean;
  createError: string | null;
  onSelectSeries: (series: DisplayDicomSeries) => void;
  actionMode?: DicomSeriesActionMode;
  allowIneligibleSelection?: boolean;
  thumbnailFallback?: React.ReactNode;
  onConfirmSelection?: (series?: DisplayDicomSeries) => void;
  onRemoveSelection?: () => void;
  isConfirmedSeries?: boolean;
  displayVariant?: 'aligned' | 'original';
  showConfirmSelection?: boolean;
  onDisplayVariantChange?: (variant: 'aligned' | 'original') => void;
  isAlignedAvailable?: boolean;
}

const SelectedSeriesBadge: React.FC = () => (
  <span
    className="dicom-selected-tag"
    role="status"
    aria-label="Selected"
    title="Selected"
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      aria-hidden="true"
    >
      <path
        d="m5 12.5 4.25 4.25L19 7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <span className="pc-visually-hidden">Selected</span>
  </span>
);

/** One selection glyph for both project creation and Viewer source pickers. */
const SeriesSelectionIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    aria-hidden="true"
    data-testid="series-selection-icon"
  >
    <path
      d="M4 6.5A2.5 2.5 0 0 1 6.5 4H10l2 2h5.5A2.5 2.5 0 0 1 20 8.5v9A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5z"
      strokeWidth={1.7}
      strokeLinejoin="round"
    />
    <path
      d="M9 13h6m-2.25-2.25L15 13l-2.25 2.25"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DicomSeriesCard: React.FC<DicomSeriesCardProps> = ({
  series,
  isSelected,
  isExpanded = isSelected,
  uploadId,
  token,
  isCreateDisabled,
  isSavingProject,
  createError,
  onSelectSeries,
  actionMode = 'create-project',
  allowIneligibleSelection = false,
  thumbnailFallback,
  onConfirmSelection,
  onRemoveSelection,
  isConfirmedSeries = false,
  selectionLocked = false,
  displayVariant,
  showConfirmSelection = true,
  onDisplayVariantChange,
  isAlignedAvailable = true,
}) => {
  const isVolumeCandidate = series.isVolumeCandidate ?? series.eligibleForFixed;
  const isCompatibleReference =
    actionMode === 'create-project'
      ? isProjectReferenceSeries(series)
      : isReferenceMrSeries(series);
  const isEligible =
    isVolumeCandidate &&
    (allowIneligibleSelection || (isCompatibleReference && series.eligibleForFixed));
  const isPrimaryHighMatch = isHighMatchSeries(series);
  const validationIssues = [...(series.geometryIssues ?? []), ...(series.pixelDataIssues ?? [])];
  const eligibilityTitle = selectionLocked
    ? 'Unlock series selection to choose another series'
    : isEligible
      ? series.title
      : validationIssues[0] || 'Not eligible for volume selection';
  const canRenderPixelPreview = Boolean(uploadId && series.eligibleForFixed && isVolumeCandidate);
  const geometryStatus = series.geometryStatus ?? 'warning';
  const geometryLabel =
    geometryStatus === 'valid'
      ? 'Geometry ✓'
      : geometryStatus === 'warning'
        ? 'Geometry ⚠'
        : 'Geometry ✕';
  const pixelDataStatus = series.pixelDataStatus ?? 'not_checked';
  const pixelDataLabel =
    pixelDataStatus === 'valid'
      ? 'Pixel ✓'
      : pixelDataStatus === 'warning'
        ? 'Pixel ⚠'
        : pixelDataStatus === 'invalid'
          ? 'Pixel ✕'
          : 'Pixel —';
  const showSelectedTag = actionMode === 'select-series' && isConfirmedSeries;
  // Confirmation persists when another card expands; source controls do not.
  const showSourceToggle =
    isExpanded &&
    actionMode === 'select-series' &&
    Boolean(displayVariant && onDisplayVariantChange);

  return (
    <div
      className={`dicom-series-item ${isExpanded ? 'selected' : ''} ${
        isPrimaryHighMatch
          ? `classification-recommendation recommendation-${series.modality.toLowerCase()}`
          : ''
      }`}
      onClick={() => {
        if (isEligible) {
          onSelectSeries(series);
        }
      }}
      title={eligibilityTitle}
      aria-disabled={!isEligible}
      data-series-id={series.id}
    >
      <div className="dicom-thumb">
        {canRenderPixelPreview ? (
          <DicomSeriesThumbnail
            uploadId={uploadId!}
            studyInstanceUid={series.studyInstanceUid}
            seriesInstanceUid={series.seriesInstanceUid}
            token={token}
            label={series.label}
            interactive={isExpanded}
            thumbnailDataUrls={series.thumbnailDataUrls}
          />
        ) : thumbnailFallback ? (
          thumbnailFallback
        ) : (
          <img
            className="dicom-thumb-fallback"
            src={noDicomIcon}
            alt={`${series.label} preview unavailable`}
            draggable={false}
          />
        )}
      </div>

      <div className="dicom-info">
        <div className="dicom-info-top">
          <span className="dicom-label">{series.label}</span>
        </div>

        <div className="dicom-meta">
          <span className="dicom-meta-icon">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <rect
                x="2"
                y="3"
                width="12"
                height="10"
                rx="1"
              />
              <path d="M5 3V2M8 3V2M11 3V2" />
            </svg>
          </span>

          <span>{series.countLabel ?? `${series.slices} slices`}</span>
          <span className="dicom-meta-dot">·</span>
          <span>{series.type}</span>

          <span
            className={`dicom-geometry-chip geometry-${geometryStatus}`}
            title={series.geometryIssues?.join('; ') || geometryLabel}
          >
            {geometryLabel}
          </span>

          <span
            className={`dicom-pixel-chip pixel-${pixelDataStatus.replace('_', '-')}`}
            title={series.pixelDataIssues?.join('; ') || pixelDataLabel}
          >
            {pixelDataLabel}
          </span>

          {series.tr && (
            <>
              <span className="dicom-meta-dot">·</span>
              <span>TR: {series.tr}</span>
            </>
          )}

          {series.te && (
            <>
              <span className="dicom-meta-dot">·</span>
              <span>TE: {series.te}</span>
            </>
          )}

          {series.dim && (
            <>
              <span className="dicom-meta-dot">·</span>
              <span>{series.dim}</span>
            </>
          )}
        </div>
      </div>

      <div className={`dicom-action ${showSourceToggle ? 'has-source-toggle' : ''}`}>
        {showSourceToggle && (
          <div className="dicom-selected-source-row">
            <div
              className="dicom-series-display-toggle"
              role="group"
              aria-label={`${series.modality} display source`}
            >
              {(['aligned', 'original'] as const).map(variant => (
                <button
                  key={variant}
                  type="button"
                  className={displayVariant === variant ? 'active' : ''}
                  aria-pressed={displayVariant === variant}
                  disabled={variant === 'aligned' && !isAlignedAvailable}
                  title={
                    variant === 'aligned' && !isAlignedAvailable
                      ? 'Automatic Orientation is still being prepared'
                      : undefined
                  }
                  onClick={event => {
                    event.stopPropagation();
                    onDisplayVariantChange?.(variant);
                  }}
                >
                  {getNiftiAlignmentLabel(
                    { variant, alignment_mode: series.alignedNiftiAsset?.alignment_mode },
                    series.hasUnsavedAlignment
                  )}
                </button>
              ))}
            </div>
            {showSelectedTag && <SelectedSeriesBadge />}
          </div>
        )}

        <div className="dicom-classification-stack">
          {(series.modality === 'T1w' || series.modality === 'T2w') && (
            <span
              className={`dicom-match-label ${
                isPrimaryHighMatch ? 'match-high' : 'match-uncertain'
              }`}
            >
              {isPrimaryHighMatch ? 'Recommanded' : 'Uncertain'}
            </span>
          )}
          <span className={`dicom-modality-badge ${MODALITY_COLORS[series.modality]}`}>
            {series.modality}
          </span>
        </div>

        {showSelectedTag && !showSourceToggle && <SelectedSeriesBadge />}

        {isExpanded && showConfirmSelection && !showSelectedTag && (
          <>
            <button
              type={actionMode === 'create-project' ? 'submit' : 'button'}
              className={`modal-submit-button modal-submit-icon-button dicom-expanded-create-button ${
                isSavingProject ? 'is-saving' : ''
              }`}
              disabled={
                selectionLocked ||
                !isEligible ||
                isSavingProject ||
                (actionMode === 'create-project' && isCreateDisabled)
              }
              aria-label={
                actionMode === 'select-series' && onRemoveSelection
                  ? 'Remove selected MR'
                  : actionMode === 'select-series'
                    ? 'Use selected series'
                    : isSavingProject
                      ? 'Preparing project data'
                      : 'Create project'
              }
              title={
                actionMode === 'select-series' && onRemoveSelection
                  ? 'Remove selected MR'
                  : actionMode === 'select-series'
                    ? 'Use selected series'
                    : isSavingProject
                      ? 'Preparing project data'
                      : 'Create project'
              }
              onClick={event => {
                event.stopPropagation();
                if (selectionLocked || !isEligible) {
                  return;
                }
                if (actionMode === 'select-series') {
                  if (onRemoveSelection) {
                    onRemoveSelection();
                  } else {
                    onConfirmSelection?.(series);
                  }
                }
              }}
            >
              {actionMode === 'select-series' && onRemoveSelection ? (
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    d="M6 6l12 12M18 6L6 18"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                </svg>
              ) : actionMode === 'select-series' ? (
                <>
                  <SeriesSelectionIcon />
                  <span className="pc-visually-hidden">Select</span>
                </>
              ) : isSavingProject ? (
                <span
                  className="modal-submit-spinner"
                  aria-hidden="true"
                />
              ) : (
                <>
                  <SeriesSelectionIcon />
                  <span className="pc-visually-hidden">Select</span>
                </>
              )}
            </button>
            {actionMode === 'create-project' && createError && (
              <p className="dicom-expanded-create-error">{createError}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const DicomSeriesPanel: React.FC<DicomSeriesPanelProps> = ({
  isSelectionLocked = false,
  imageSource = 'dicom',
  uploadedFile,
  uploadStatus,
  uploadProgress,
  uploadPhase = uploadProgress < 100 ? 'uploading' : 'analyzing',
  uploadError,
  uploadId,
  token,
  seriesList,
  selectedFixedSeriesKey,
  expandedSeriesKey,
  fileInputRef,
  folderInputRef,
  onSelectSeries,
  onFileChange,
  onFolderInputChange,
  onDrop,
  onDragOver,
  onUploadAreaClick,
  onRemoveUpload,
  isCreateDisabled,
  isSavingProject,
  createError,
  className,
  actionMode = 'create-project',
  allowIneligibleSelection = false,
  thumbnailFallback,
  onConfirmSelection,
  onRemoveSelection,
  promotedSeriesKey = null,
  isUploadRemovable = true,
  seriesTypeLabel = 'MR',
  confirmedSeriesKey = null,
  removeActionIcon = 'close',
  displayVariant,
  onDisplayVariantChange,
  isAlignedAvailable = true,
}) => {
  const [openStudyIds, setOpenStudyIds] = useState<Set<string>>(() => new Set());
  const [showUploadSourceMenu, setShowUploadSourceMenu] = useState(false);
  const uploadAreaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showUploadSourceMenu) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!uploadAreaRef.current?.contains(event.target as Node)) {
        setShowUploadSourceMenu(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowUploadSourceMenu(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showUploadSourceMenu]);

  const promotedSeries = useMemo(
    () => seriesList.find(series => series.id === promotedSeriesKey) ?? null,
    [promotedSeriesKey, seriesList]
  );
  const listedSeries = useMemo(
    () => seriesList.filter(series => series.id !== promotedSeriesKey),
    [promotedSeriesKey, seriesList]
  );
  const studyGroups = useMemo(() => groupReferenceSeriesByStudy(listedSeries), [listedSeries]);
  const defaultOpenStudyId = useMemo(() => getDefaultOpenStudyId(studyGroups), [studyGroups]);
  const studyLayoutKey = studyGroups
    .map(group => `${group.id}:${group.series.map(series => series.id).join(',')}`)
    .join('|');

  useEffect(() => {
    setOpenStudyIds(defaultOpenStudyId ? new Set([defaultOpenStudyId]) : new Set());
  }, [defaultOpenStudyId, studyLayoutKey]);

  const toggleStudy = (studyInstanceUid: string) => {
    setOpenStudyIds(current => {
      const next = new Set(current);
      if (next.has(studyInstanceUid)) {
        next.delete(studyInstanceUid);
      } else {
        next.add(studyInstanceUid);
      }
      return next;
    });
  };

  const sourceLabel = imageSource === 'nifti' ? 'NIfTI' : 'medical image';
  const transferTitle: Record<DicomTransferPhase, string> = {
    uploading: `Uploading ${sourceLabel}`,
    analyzing: `Analyzing ${sourceLabel}`,
    downloading: `Downloading ${sourceLabel}`,
  };
  const emptyTitle =
    uploadStatus === 'uploading'
      ? transferTitle[uploadPhase]
      : uploadStatus === 'error'
        ? `${sourceLabel} analysis failed`
        : 'No image data selected';
  const showDicomLoadingMedia = imageSource === 'dicom' && uploadStatus === 'uploading';

  return (
    <section className={`series-panel ${className ?? ''}`.trim()}>
      <div className="series-upload-block">
        {promotedSeries ? (
          <div
            className="selected-series-summary"
            data-series-id={promotedSeries.id}
          >
            <div className="selected-series-summary-preview">
              {uploadId && promotedSeries.eligibleForFixed ? (
                <DicomSeriesThumbnail
                  uploadId={uploadId}
                  studyInstanceUid={promotedSeries.studyInstanceUid}
                  seriesInstanceUid={promotedSeries.seriesInstanceUid}
                  token={token}
                  label={promotedSeries.label}
                  interactive
                  thumbnailDataUrls={promotedSeries.thumbnailDataUrls}
                />
              ) : (
                (thumbnailFallback?.(promotedSeries) ?? (
                  <img
                    className="dicom-thumb-fallback"
                    src={noDicomIcon}
                    alt={`${promotedSeries.label} preview unavailable`}
                    draggable={false}
                  />
                ))
              )}
            </div>

            <div className="selected-series-summary-details">
              <div className="selected-series-summary-heading">
                <div className="selected-series-summary-title-group">
                  <p className="selected-series-summary-title">{promotedSeries.label}</p>
                  {promotedSeries.studyDescription && (
                    <span className="selected-series-summary-study">
                      {promotedSeries.studyDescription}
                    </span>
                  )}
                </div>
              </div>

              <div className="selected-series-summary-meta">
                <span
                  className={`dicom-modality-badge ${MODALITY_COLORS[promotedSeries.modality]}`}
                >
                  {promotedSeries.modality}
                </span>
                <span>{promotedSeries.countLabel ?? `${promotedSeries.slices} slices`}</span>
                <span>{promotedSeries.type}</span>
                <span>
                  {promotedSeries.geometryStatus === 'valid'
                    ? 'Geometry ✓'
                    : promotedSeries.geometryStatus === 'invalid'
                      ? 'Geometry ✕'
                      : 'Geometry ⚠'}
                </span>
                {promotedSeries.tr && <span>TR: {promotedSeries.tr}</span>}
                {promotedSeries.te && <span>TE: {promotedSeries.te}</span>}
                {promotedSeries.dim && <span>{promotedSeries.dim}</span>}
                {(promotedSeries.modality === 'T1w' || promotedSeries.modality === 'T2w') && (
                  <span
                    className={isHighMatchSeries(promotedSeries) ? 'match-high' : 'match-uncertain'}
                  >
                    {isHighMatchSeries(promotedSeries) ? 'Recommanded' : 'Uncertain'}
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              className="selected-series-summary-remove"
              aria-label="Remove selected MR"
              title="Remove selected MR"
              onClick={onRemoveSelection}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  d={
                    removeActionIcon === 'trash'
                      ? 'M4 7h16M9 7V4h6v3M6.5 7l.75 13h9.5l.75-13M10 11v5M14 11v5'
                      : 'M6 6l12 12M18 6L6 18'
                  }
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        ) : !uploadedFile ? (
          <div
            ref={uploadAreaRef}
            className="file-upload-area file-upload-area-right"
            role="button"
            tabIndex={0}
            aria-haspopup="menu"
            aria-expanded={showUploadSourceMenu}
            aria-label="Upload DICOM or NIfTI files or a folder"
            onDrop={onDrop}
            onDragOver={onDragOver}
            onClick={() => {
              setShowUploadSourceMenu(current => !current);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                setShowUploadSourceMenu(current => !current);
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              id="file-upload"
              className="file-input-hidden"
              multiple
              onClick={event => event.stopPropagation()}
              onChange={onFileChange}
            />
            <input
              ref={folderInputRef}
              type="file"
              hidden
              multiple
              {...DIRECTORY_INPUT_ATTRIBUTES}
              onClick={event => event.stopPropagation()}
              onChange={onFolderInputChange}
            />

            <div className="file-upload-label file-upload-label-right">
              <svg
                className="upload-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>

              <span className="upload-text">Upload DICOM or NIfTI</span>
              <span className="upload-subtext">
                Click to choose files or a folder, or drag either one here
              </span>
            </div>

            {showUploadSourceMenu && (
              <div
                className="upload-source-menu"
                role="menu"
                aria-label="Choose upload source"
              >
                <button
                  type="button"
                  className="upload-source-menu-item"
                  role="menuitem"
                  onClick={event => {
                    event.stopPropagation();
                    setShowUploadSourceMenu(false);
                    onUploadAreaClick();
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 3h8l4 4v14H6V3zm8 0v5h5M9 13h6M9 17h6"
                      strokeWidth={1.8}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Files
                </button>
                <button
                  type="button"
                  className="upload-source-menu-item"
                  role="menuitem"
                  onClick={event => {
                    event.stopPropagation();
                    setShowUploadSourceMenu(false);
                    if (uploadStatus !== 'uploading') {
                      folderInputRef.current?.click();
                    }
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                      strokeWidth={1.8}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Folder
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="uploaded-file-card uploaded-file-card-right">
            <div className="uploaded-file-icon">
              <svg
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>

            <div className="uploaded-file-info">
              <p className="uploaded-file-name">{uploadedFile.name}</p>
              <p className="uploaded-file-size">
                {uploadedFile.size > 0 ? formatSize(uploadedFile.size) : null}
                {uploadedFile.size > 0 && uploadedFile.count > 1 ? ' · ' : null}
                {uploadedFile.count > 1
                  ? `${uploadedFile.count} files`
                  : uploadedFile.size === 0
                    ? '1 file'
                    : null}
              </p>
              {uploadStatus !== 'success' && (
                <p className="uploaded-file-meta">
                  {uploadStatus === 'uploading'
                    ? uploadPhase === 'uploading'
                      ? `Uploading ${sourceLabel} data · ${uploadProgress}%`
                      : transferTitle[uploadPhase]
                    : (uploadError ?? `${sourceLabel} analysis failed`)}
                </p>
              )}

              {uploadStatus === 'uploading' && (
                <div
                  className="upload-progress"
                  role="progressbar"
                  aria-label={`${sourceLabel} upload progress`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={uploadProgress}
                >
                  <span
                    className="upload-progress-bar"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              )}
            </div>

            {isUploadRemovable && (
              <button
                type="button"
                className="uploaded-file-remove"
                disabled={isSelectionLocked}
                aria-label={`Remove ${sourceLabel} upload`}
                title={`Remove ${sourceLabel} upload`}
                onClick={onRemoveUpload}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    d={
                      removeActionIcon === 'trash'
                        ? 'M4 7h16M9 7V4h6v3M6.5 7l.75 13h9.5l.75-13M10 11v5M14 11v5'
                        : 'M6 6l12 12M18 6L6 18'
                    }
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {listedSeries.length === 0 && !promotedSeries ? (
        <div className="series-empty-state">
          {showDicomLoadingMedia ? (
            <img
              className="series-empty-media"
              src={`${publicUrl}idle.gif`}
              alt=""
              role="status"
              aria-label={transferTitle[uploadPhase]}
              draggable={false}
            />
          ) : (
            <div
              className={`series-empty-icon ${uploadStatus === 'uploading' ? 'is-transferring' : ''}`}
              role={uploadStatus === 'uploading' ? 'status' : undefined}
              aria-label={uploadStatus === 'uploading' ? transferTitle[uploadPhase] : undefined}
            >
              <svg
                data-testid="waiting-for-image-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M6 3h12M6 21h12M7 3v3a5 5 0 0 0 2 4l3 2-3 2a5 5 0 0 0-2 4v3M17 3v3a5 5 0 0 1-2 4l-3 2 3 2a5 5 0 0 1 2 4v3M9 18h6"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
          <p className="series-empty-title">{emptyTitle}</p>
        </div>
      ) : listedSeries.length > 0 ? (
        <div
          className="dicom-series-list"
          aria-label={`${seriesTypeLabel} studies`}
        >
          {studyGroups.map(group => {
            const isOpen = openStudyIds.has(group.id);
            const contentId = `dicom-study-${group.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

            return (
              <section
                key={group.id}
                className="dicom-study-accordion"
                data-study-instance-uid={group.id}
              >
                <button
                  type="button"
                  className="dicom-study-toggle"
                  aria-expanded={isOpen}
                  aria-controls={contentId}
                  onClick={() => toggleStudy(group.id)}
                >
                  <span
                    className="dicom-study-chevron"
                    aria-hidden="true"
                  >
                    {isOpen ? '−' : '+'}
                  </span>
                  <span className="dicom-study-toggle-main">
                    <strong>{group.label}</strong>
                    <span className="dicom-study-modality-summary">{group.modalitySummary}</span>
                  </span>
                  {!isOpen && group.highMatchModalities.length > 0 && (
                    <span className="dicom-study-high-match-summary">
                      {group.highMatchModalities.join(' + ')} · Recommanded
                    </span>
                  )}
                  <span className="dicom-study-series-count">{group.series.length} series</span>
                </button>

                {isOpen && (
                  <div
                    id={contentId}
                    className="dicom-series-category-content dicom-study-content"
                    role="region"
                  >
                    {group.series.map(series => (
                      <DicomSeriesCard
                        key={series.id}
                        series={series}
                        isSelected={selectedFixedSeriesKey === series.id}
                        isExpanded={(expandedSeriesKey ?? selectedFixedSeriesKey) === series.id}
                        uploadId={uploadId}
                        token={token}
                        isCreateDisabled={isCreateDisabled}
                        isSavingProject={isSavingProject}
                        createError={createError}
                        onSelectSeries={onSelectSeries}
                        actionMode={actionMode}
                        allowIneligibleSelection={allowIneligibleSelection}
                        thumbnailFallback={thumbnailFallback?.(series)}
                        onConfirmSelection={onConfirmSelection}
                        onRemoveSelection={promotedSeries ? undefined : onRemoveSelection}
                        isConfirmedSeries={confirmedSeriesKey === series.id}
                        selectionLocked={isSelectionLocked && confirmedSeriesKey !== series.id}
                        displayVariant={displayVariant}
                        showConfirmSelection
                        onDisplayVariantChange={onDisplayVariantChange}
                        isAlignedAvailable={isAlignedAvailable}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

const NewProjectModal: React.FC<NewProjectModalProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { accessToken, logout } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const uploadIdRef = useRef<string | null>(null);
  const shouldPreserveUploadRef = useRef(false);

  const [fullName, setFullName] = useState('');
  const [imageSource, setImageSource] = useState<ProjectImageSource>('dicom');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [uploadedFile, setUploadedFile] = useState<UploadedFileMeta | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<DicomTransferPhase>('uploading');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<DicomAnalyzeResponse | null>(null);
  const [selectedFixedSeries, setSelectedFixedSeries] = useState<SelectedFixedSeries | null>(null);
  const [uploadAbortController, setUploadAbortController] = useState<AbortController | null>(null);
  const [isNameAutofilled, setIsNameAutofilled] = useState(false);
  const [isDobAutofilled, setIsDobAutofilled] = useState(false);
  const [isGenderAutofilled, setIsGenderAutofilled] = useState(false);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [patientPhoto, setPatientPhoto] = useState<File | null>(null);

  useEffect(() => {
    uploadAbortControllerRef.current = uploadAbortController;
  }, [uploadAbortController]);

  useEffect(() => {
    uploadIdRef.current = uploadId;
  }, [uploadId]);

  useEffect(() => {
    return () => {
      if (shouldPreserveUploadRef.current) {
        return;
      }
      uploadAbortControllerRef.current?.abort();
      if (uploadIdRef.current && accessToken) {
        void deleteDicomUpload(uploadIdRef.current, accessToken).catch(() => undefined);
      }
    };
  }, [accessToken]);

  const modalSeries = useMemo(
    () =>
      analysisResult
        ? analysisResult.series.map((series, index) => mapApiSeriesToDisplaySeries(series, index))
        : [],
    [analysisResult]
  );

  const selectedFixedSeriesRecord = useMemo(
    () =>
      analysisResult?.series.find(
        series =>
          series.series_instance_uid === selectedFixedSeries?.seriesInstanceUid &&
          series.study_instance_uid === selectedFixedSeries?.studyInstanceUid &&
          mapApiSeriesToDisplaySeries(series, 0).eligibleForFixed
      ) ?? null,
    [analysisResult, selectedFixedSeries]
  );

  const selectedFixedSeriesKey = useMemo(
    () =>
      selectedFixedSeries
        ? buildDicomSeriesKey(
            selectedFixedSeries.studyInstanceUid,
            selectedFixedSeries.seriesInstanceUid
          )
        : null,
    [selectedFixedSeries]
  );

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
  };

  const resetModalState = () => {
    shouldPreserveUploadRef.current = false;
    setFullName('');
    setImageSource('dicom');
    setGender('');
    setDob('');
    setUploadedFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setUploadPhase('uploading');
    setUploadError(null);
    setUploadId(null);
    setAnalysisResult(null);
    setSelectedFixedSeries(null);
    setUploadAbortController(null);
    setIsNameAutofilled(false);
    setIsDobAutofilled(false);
    setIsGenderAutofilled(false);
    setIsSavingProject(false);
    setCreateError(null);
    setPatientPhoto(null);
    uploadAbortControllerRef.current = null;
    uploadIdRef.current = null;
    clearFileInput();
  };

  const cleanupUploadState = async () => {
    const currentController = uploadAbortControllerRef.current;
    currentController?.abort();

    const currentUploadId = uploadIdRef.current;
    if (currentUploadId && accessToken) {
      try {
        await deleteDicomUpload(currentUploadId, accessToken);
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          logout();
        }
      }
    }

    resetModalState();
  };

  const applyAnalysisResult = (result: DicomAnalyzeResponse) => {
    setAnalysisResult(result);
    setUploadId(result.upload_id);
    uploadIdRef.current = result.upload_id;

    const nextName = result.patient.patient_name ?? '';
    const nextDob = formatDateDDMMYYYY(result.patient.birth_date, '');
    const nextGender = mapPatientSexToGender(result.patient.sex);

    setFullName(nextName);
    setDob(nextDob);
    setGender(nextGender);
    setIsNameAutofilled(Boolean(result.patient.patient_name));
    setIsDobAutofilled(Boolean(result.patient.birth_date));
    setIsGenderAutofilled(Boolean(nextGender));
    const onlySeries = result.source_type === 'nifti' ? result.series[0] : null;
    setSelectedFixedSeries(
      onlySeries
        ? {
            studyInstanceUid: onlySeries.study_instance_uid,
            seriesInstanceUid: onlySeries.series_instance_uid,
          }
        : null
    );
    setUploadError(null);
    setCreateError(null);
    setUploadStatus('success');
  };

  const processSelectedFiles = async (files: File[], source: UploadSource) => {
    if (!accessToken || files.length === 0 || uploadStatus === 'uploading') {
      return;
    }

    const niftiFiles = files.filter(file => /\.nii(?:\.gz)?$/i.test(file.name));
    const isNiftiUpload = files.length === 1 && niftiFiles.length === 1;
    if (niftiFiles.length > 0 && !isNiftiUpload) {
      setUploadedFile(files.length ? buildUploadMeta(files, source) : null);
      setUploadStatus('error');
      setUploadError('Upload one NIfTI volume by itself, or upload only DICOM files.');
      return;
    }

    const detectedImageSource: ProjectImageSource = isNiftiUpload ? 'nifti' : 'dicom';
    setImageSource(detectedImageSource);

    const controller = new AbortController();
    setUploadAbortController(controller);
    uploadAbortControllerRef.current = controller;
    setUploadStatus('uploading');
    setUploadProgress(0);
    setUploadPhase('uploading');
    setUploadError(null);
    setUploadId(null);
    uploadIdRef.current = null;
    setAnalysisResult(null);
    setSelectedFixedSeries(null);
    setFullName('');
    setDob('');
    setGender('');
    setIsNameAutofilled(false);
    setIsDobAutofilled(false);
    setIsGenderAutofilled(false);
    setUploadedFile(buildUploadMeta(files, source));

    try {
      const result =
        detectedImageSource === 'nifti'
          ? await analyzeNiftiFile(
              files[0],
              accessToken,
              controller.signal,
              setUploadProgress,
              setUploadPhase
            )
          : await analyzeDicomFiles(
              files,
              accessToken,
              controller.signal,
              setUploadProgress,
              setUploadPhase
            );
      applyAnalysisResult(result);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        logout();
        return;
      }

      if (isAbortError(error)) {
        setUploadStatus('idle');
        setUploadError(null);
        return;
      }

      setUploadStatus('error');
      setUploadError(mapUploadErrorMessage(error));
      setAnalysisResult(null);
      setUploadId(null);
      uploadIdRef.current = null;
      setSelectedFixedSeries(null);
    } finally {
      if (uploadAbortControllerRef.current === controller) {
        uploadAbortControllerRef.current = null;
      }
      setUploadAbortController(null);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (uploadStatus === 'uploading') {
      event.target.value = '';
      return;
    }
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = '';
    void processSelectedFiles(selectedFiles, 'files');
  };

  const handleFolderInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (uploadStatus === 'uploading') {
      event.target.value = '';
      return;
    }

    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length === 0) {
      event.target.value = '';
      return;
    }

    void processSelectedFiles(selectedFiles, 'folder');
    event.target.value = '';
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (uploadStatus === 'uploading') {
      return;
    }

    const droppedFiles = await collectDroppedFiles(event.dataTransfer);
    void processSelectedFiles(droppedFiles, 'drop');
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUploadAreaClick = () => {
    if (uploadStatus === 'uploading') {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleModalClose = async () => {
    if (isSavingProject) {
      return;
    }
    shouldPreserveUploadRef.current = false;
    await cleanupUploadState();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !analysisResult ||
      !selectedFixedSeriesRecord ||
      !selectedFixedSeries ||
      !uploadId ||
      !accessToken ||
      isSavingProject
    ) {
      return;
    }

    const fixedStudyInstanceUid = selectedFixedSeries.studyInstanceUid;
    const fixedSeriesInstanceUid = selectedFixedSeries.seriesInstanceUid;

    setIsSavingProject(true);
    setCreateError(null);
    let createdPatient = null;
    let preparedNiftiAssets: PreparedNiftiAsset[] = [];
    let alignedNiftiAssets: PreparedNiftiAsset[] = [];
    try {
      if (patientPhoto) {
        await uploadPatientPhoto(uploadId, patientPhoto, accessToken);
      }
      preparedNiftiAssets = analysisResult.prepared_nifti_assets ?? [];
      const preparedSelectedSeries = await prepareSelectedSeriesData(
        uploadId,
        fixedStudyInstanceUid,
        fixedSeriesInstanceUid,
        accessToken
      );
      const originalAssetsBySeries = new Map(
        preparedNiftiAssets.map(asset => [asset.series_instance_uid, asset] as const)
      );
      preparedSelectedSeries.assets.forEach(asset =>
        originalAssetsBySeries.set(asset.series_instance_uid, asset)
      );
      preparedNiftiAssets = Array.from(originalAssetsBySeries.values());
      const alignedAssetsBySource = new Map(
        (analysisResult.aligned_nifti_assets ?? []).map(asset => [
          asset.source_asset_id ?? asset.asset_id,
          asset,
        ])
      );
      preparedSelectedSeries.aligned_assets.forEach(asset =>
        alignedAssetsBySource.set(asset.source_asset_id ?? asset.asset_id, asset)
      );
      alignedNiftiAssets = Array.from(alignedAssetsBySource.values());
      const selectedNiftiAsset = preparedNiftiAssets.find(
        asset => asset.series_instance_uid === fixedSeriesInstanceUid
      );
      if (!selectedNiftiAsset) {
        throw new Error('The selected NIfTI series is unavailable on the server.');
      }
      // Open every newly selected project from the diagnostic source volume.
      // Aligned assets remain available after the background pipeline finishes.
      await cachePreparedNiftiAsset(selectedNiftiAsset, accessToken);
      createdPatient = await createPatient(
        {
          upload_id: uploadId,
          patient_name: fullName.trim(),
          birth_date: toApiDate(dob),
          gender: gender.trim(),
          study_instance_uid: fixedStudyInstanceUid,
          fixed_series_instance_uid: fixedSeriesInstanceUid,
          dicom_studies: analysisResult.studies,
          dicom_series: analysisResult.series,
        },
        accessToken
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        logout();
        return;
      }
      setCreateError(error instanceof Error ? error.message : 'Project could not be created.');
      setIsSavingProject(false);
      return;
    }

    if (!createdPatient) {
      return;
    }

    shouldPreserveUploadRef.current = true;
    onClose();
    navigate('/project-creation', {
      state: {
        openedFrom: 'new-project',
        uploadId,
        study: {
          id: fixedStudyInstanceUid,
          patientName: fullName,
          dob,
          gender,
          studyId: fixedStudyInstanceUid,
          studyInstanceUid: fixedStudyInstanceUid,
          patientId: createdPatient.id,
          hasPhoto: Boolean(createdPatient.has_photo || patientPhoto),
        },
        dicomStudies: analysisResult.studies,
        fixedSeriesInstanceUid,
        fixedStudyInstanceUid,
        dicomSeries: analysisResult.series,
        preparedNiftiAssets,
        alignedNiftiAssets,
        alignmentStatus:
          analysisResult.source_type === 'nifti' && alignedNiftiAssets.length > 0
            ? 'ready'
            : (analysisResult.alignment_status ?? 'ready'),
      },
    });
  };

  const isCreateDisabled =
    uploadStatus !== 'success' ||
    !analysisResult ||
    !fullName.trim() ||
    !dob.trim() ||
    !gender.trim() ||
    !selectedFixedSeries ||
    !selectedFixedSeriesRecord ||
    isSavingProject;

  return (
    <div className="modal-overlay modal-overlay-wide">
      <div className="modal-container modal-container-wide">
        <div className="modal-header">
          <h2 className="modal-title">Create New Project</h2>
          <button
            type="button"
            className="modal-close-button"
            aria-label="Close project creation"
            title="Close"
            disabled={isSavingProject}
            onClick={() => void handleModalClose()}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form
          className="modal-form"
          onSubmit={handleSubmit}
        >
          <div className="modal-content-grid">
            <ProjectInfoPanel
              fullName={fullName}
              dob={dob}
              gender={gender}
              isNameAutofilled={isNameAutofilled}
              isDobAutofilled={isDobAutofilled}
              isGenderAutofilled={isGenderAutofilled}
              patientPhoto={patientPhoto}
              showHeading={false}
              onFullNameChange={value => {
                setIsNameAutofilled(false);
                setFullName(value);
              }}
              onDobChange={value => {
                setIsDobAutofilled(false);
                setDob(value);
              }}
              onGenderChange={value => {
                setIsGenderAutofilled(false);
                setGender(value);
              }}
              onPatientPhotoChange={setPatientPhoto}
            />

            <DicomSeriesPanel
              imageSource={imageSource}
              uploadedFile={uploadedFile}
              uploadStatus={uploadStatus}
              uploadPhase={uploadPhase}
              uploadProgress={uploadProgress}
              uploadError={uploadError}
              uploadId={uploadId}
              token={accessToken ?? ''}
              seriesList={modalSeries}
              selectedFixedSeriesKey={selectedFixedSeriesKey}
              fileInputRef={fileInputRef}
              folderInputRef={folderInputRef}
              onSelectSeries={series => {
                if (!isProjectReferenceSeries(series) || !series.eligibleForFixed) return;
                setSelectedFixedSeries({
                  studyInstanceUid: series.studyInstanceUid,
                  seriesInstanceUid: series.seriesInstanceUid,
                });
              }}
              onFileChange={handleFileChange}
              onFolderInputChange={handleFolderInputChange}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onUploadAreaClick={handleUploadAreaClick}
              onRemoveUpload={() => {
                void cleanupUploadState();
              }}
              isCreateDisabled={isCreateDisabled}
              isSavingProject={isSavingProject}
              createError={createError}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewProjectModal;
